import React, {useMemo, useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {useQuery} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import type {NativeStackScreenProps} from '@react-navigation/native-stack';

import type {ReportsStackParamList} from '@/app/routes';
import {
  exportCompanyAttendance,
  fetchCompanyAttendance,
  fetchControlDepartments,
} from '@/entities/attendance/api';
import {
  currentMonth,
  periodKey,
  periodLength,
  periodParam,
  type Period,
} from '@/entities/attendance/period';
import {useSalaryAccess} from '@/entities/permission/access';
import {useAuthStore} from '@/entities/session/auth-store';
import {ContextPlaceholder} from '@/widgets/context-placeholder/ContextPlaceholder';
import {
  dailyTotals,
  employeeAccessors,
  employeeTiebreak,
  rosterBreakdown,
  type IssueFilter,
} from '@/features/view-attendance/lib/aggregate';
import {formatCount} from '@/shared/lib/format';
import {sortRows} from '@/shared/lib/table-sort';
import {
  boolParam,
  enumParam,
  listParam,
  useParamState,
} from '@/shared/lib/use-param-state';
import {daysCsv, employeesCsv} from '@/features/view-attendance/lib/exports';
import {reportFileName} from '@/features/view-attendance/lib/csv';
import {
  describePeriod,
  usePeriodLabel,
} from '@/features/view-attendance/lib/period-label';
import {AttendanceKpi} from '@/features/view-attendance/ui/AttendanceKpi';
import {DailyChart} from '@/features/view-attendance/ui/DailyChart';
import {DepartmentRanking} from '@/features/view-attendance/ui/DepartmentRanking';
import {EmployeeReportList} from '@/features/view-attendance/ui/EmployeeReportList';
import {TimesheetPicker} from '@/features/export-timesheet/ui/TimesheetPicker';
import {ExportBar} from '@/features/view-attendance/ui/ExportBar';
import {ReportToolbar} from '@/features/view-attendance/ui/ReportToolbar';
import {AppButton} from '@/shared/ui/button';
import {Card} from '@/shared/ui/card';
import {Disclosure} from '@/shared/ui/disclosure';
import {EmptyState} from '@/shared/ui/empty-state';
import {HelpNote} from '@/shared/ui/explain';
import {Page} from '@/shared/ui/page';
import {Segmented} from '@/shared/ui/segmented';
import {Section} from '@/shared/ui/section';
import {Skeleton} from '@/shared/ui/skeleton';
import {AppText} from '@/shared/ui/text';
import {radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * «Посещаемость» — верхняя ступень лестницы компания → отдел → сотрудник, и владелец
 * периода для всех трёх (в отдел и в человека период едет параметром, поэтому шаг
 * вглубь никогда не меняет молча то, что сравнивают).
 *
 * ## Порядок страницы — это порядок вопросов
 *
 *   1. За что смотрим — две плашки в `ReportToolbar`, а не форма над данными.
 *   2. «Ну и как?» — приговор одной фразой и три числа (`SummaryBoard`), девять
 *      сырых счётчиков свёрнуты под свой раскрыватель.
 *   3. «С кем разбираться?» — `AttentionList`, во всю ширину, с причиной прямо в
 *      строке, а не выводимой из процента.
 *   4. «Как шёл период?» — дневной график.
 *   5. «Где именно?» и «Кто ещё?» — отделы и полный состав, закрытыми секциями,
 *      которые в закрытом виде говорят, что внутри.
 *
 * Ничего не спрятано за вкладками, поэтому ничего не невидимо; а пункт 5 остаётся
 * дешёвым, потому что закрытый `Disclosure` не монтирует своё тело.
 *
 * ## Ведомость — это второй сегмент, а не пункт в «Ещё»
 *
 * Начислено за тот же период, что и отходили; вопрос «сколько вышло по явке» задают
 * сразу за вопросом «как ходили». Раньше ведомость лежала в общей свалке «Ещё», и её
 * находили те, кто знал, что она есть.
 *
 * ## Одно решение про скорость, которое стоит назвать
 *
 * `include_days` заодно кормит дневной график, и сервер честно предупреждает о цене:
 * «компания из двухсот человек за месяц — это шесть тысяч строк». Поэтому он ВЫКЛЮЧЕН
 * по умолчанию, и пустой график просит себя сам, с названной ценой.
 */
type Props = NativeStackScreenProps<ReportsStackParamList, 'ReportsOverview'>;

/** Стабильная пустая выборка: иначе «отделы не заданы» менялo бы ссылку каждый рендер. */
const ALL_DEPARTMENTS: string[] = [];

/** Виды отбора, которые экран умеет положить в адрес. `''` — «все». */
const ISSUE_PARAM = enumParam(
  ['', 'late', 'absent', 'earlyLeave', 'silent', 'clean'] as const,
  '' as IssueFilter,
);

/**
 * До скольких дневных строк график грузится сам.
 *
 * Дневные строки — отдельный и заведомо тяжёлый запрос: человек × день. Компании на
 * полсотни человек за месяц это полторы тысячи строк, и спрашивать её, хочет ли она
 * подождать полсекунды, — значит прятать график за кнопкой от всех, кому он достался
 * бы даром. Школе на 1304 человека те же дни стоят сорок тысяч строк, и вот там
 * кнопка честно уместна.
 */
const AUTO_DAY_ROWS = 20000;

const styles = StyleSheet.create({
  segment: {alignSelf: 'flex-start', minWidth: 260},
  offerButton: {alignSelf: 'flex-start'},
  offer: {gap: spacing(2)},
  loading: {gap: spacing(3)},
});

export function ReportsScreen({navigation}: Props) {
  const {t, i18n} = useTranslation();
  const p = useTheme();
  const {label: periodTitle} = usePeriodLabel();
  const companyId = useAuthStore(s => s.activeContext?.companyId);

  // Закрепляется один раз за монтирование: любой пресет считается от этой даты,
  // поэтому отчёт не пересчитает себя молча, если приложение оставили открытым
  // через полночь.
  const now = useMemo(() => new Date(), []);

  /*
    Период, отделы и «по дням» живут В АДРЕСЕ, а не в состоянии компонента: это и есть
    вопрос, который человек задал отчёту, — его перезагружают, им делятся и в него
    возвращаются кнопкой «назад». См. `shared/lib/use-param-state`.
  */
  const thisMonth = useMemo(() => currentMonth(now), [now]);
  const periodCodec = useMemo(() => periodParam(now, thisMonth), [now, thisMonth]);
  const [period, setPeriod] = useParamState<Period>('period', thisMonth, periodCodec);
  const [departmentIds, setDepartmentIds] = useParamState(
    'departments',
    ALL_DEPARTMENTS,
    listParam,
  );
  const [includeDays, setIncludeDays] = useParamState('days', false, boolParam);
  /*
    Отбор таблицы — тоже вопрос, а не жест: «покажи, кто пропускал в цехе на прошлой
    неделе» должно быть ссылкой. Им управляют плитки наверху, поэтому он живёт здесь, а
    не внутри списка (см. `EmployeeReportList`, проп `issue`).
  */
  const [issue, setIssue] = useParamState<IssueFilter>(
    'issue',
    '',
    ISSUE_PARAM,
  );
  const [open, setOpen] = useState<{departments: boolean}>({departments: false});

  /**
   * Единственная строка раздела, зависящая от ПРАВА, а не от вида контекста.
   * `salaries` завели отдельно от `personnel` затем, чтобы оклад видел не всякий, кто
   * видит людей, — а сегмент «Ведомость», ведущий в раздел с надписью «доступа нет»,
   * сообщил бы о её существовании ровно тем, от кого её отделяли.
   */
  const salaries = useSalaryAccess(companyId);

  const departments = useQuery({
    queryKey: ['control-departments', companyId],
    queryFn: () => fetchControlDepartments(companyId as string),
    enabled: !!companyId,
  });

  /*
    Отчёт и дневные строки к нему — ДВА запроса, а не один с флажком.

    Раньше «показать график» переключал флажок в основном запросе, и весь экран —
    плитки, таблица, всё — уходил в скелетон, пока сервер пересчитывал отчёт вместе с
    сорока тысячами дневных строк. Разделив их, экран приезжает быстро всегда, а
    график догружается под собой и никого не задерживает.
  */
  const report = useQuery({
    queryKey: [
      'attendance-company',
      companyId,
      periodKey(period),
      departmentIds.join(','),
    ],
    queryFn: () =>
      fetchCompanyAttendance(companyId as string, period, {
        departmentIds,
        includeDays: false,
      }),
    enabled: !!companyId,
  });

  const employees = report.data?.employees ?? [];
  /*
    Разложение выборки по ЛЮДЯМ — то, чего сумме дней не видно.

    Ключ — `report.data`, а не `employees`: `?? []` рождает новый массив на каждый
    рендер и убил бы мемоизацию.
  */
  const roster = useMemo(
    () => rosterBreakdown(report.data?.employees ?? []),
    [report.data],
  );

  /*
    Дневные строки. Сами по себе — и только когда их объём того стоит: сколько людей в
    выборке, известно уже из отчёта выше, так что решение принимается по факту, а не по
    зашитому «компания в двести человек».
  */
  const estimatedDayRows = employees.length * periodLength(period);
  const wantDays = includeDays || (estimatedDayRows > 0 && estimatedDayRows <= AUTO_DAY_ROWS);
  const daily = useQuery({
    queryKey: [
      'attendance-company-days',
      companyId,
      periodKey(period),
      departmentIds.join(','),
    ],
    queryFn: () =>
      fetchCompanyAttendance(companyId as string, period, {
        departmentIds,
        includeDays: true,
      }),
    enabled: !!companyId && wantDays,
  });

  const days = daily.data?.days ?? [];
  // Свёртка сорока тысяч дневных строк не бесплатна. Ключ — `daily.data` по тому же
  // доводу, что и у разбивки выше.
  const totals = useMemo(() => dailyTotals(daily.data?.days ?? []), [daily.data]);

  if (!companyId) {
    return <ContextPlaceholder titleKey="nav.attendance" />;
  }

  const periodLabel = periodTitle(report.data ?? describePeriod(period));
  const deptList = departments.data?.departments ?? [];

  const fileStem = reportFileName([t('reports.fileAttendance'), periodLabel], '').slice(0, -1);

  /** Что именно читают — одной строкой под заголовком. */
  const scopeLine = report.data
    ? [
        t('reports.headcount', {count: employees.length}),
        departmentIds.length > 0
          ? t('reports.filteredBy', {count: departmentIds.length})
          : t('reports.allDepartmentsScope'),
      ].join('  ·  ')
    : t('reports.subtitle');

  const exportSlot = (
    <ExportBar
      fileName={fileStem}
      // «По дням» — когда дневные строки уже привезены; иначе выгружать нечего, и
      // предлагать их было бы обещанием файла, которого не будет.
      contents={t(days.length > 0 ? 'reports.exportDays' : 'reports.exportEmployees', {
        period: periodLabel,
        count: days.length > 0 ? days.length : employees.length,
      })}
      // Серверный файл — весь период целиком, тремя листами. Местный CSV рядом:
      // он выгружает ровно то, что человек видит на экране, с его сортировкой и
      // фильтрами, и это другой вопрос.
      onServer={format =>
        exportCompanyAttendance(companyId, period, departmentIds, format, i18n.language)
      }
      onLocal={() =>
        days.length > 0
          ? daysCsv(days, t)
          : employeesCsv(
              // Файл всегда по алфавиту, независимо от того, чем список упорядочен на
              // экране: выгрузку открывают в таблице и ищут в ней глазами.
              sortRows(
                employees,
                {key: 'name', direction: 'asc'},
                employeeAccessors(),
                employeeTiebreak,
              ),
              t,
            )
      }
    />
  );

  /**
   * Табель — третий способ унести отчёт, и единственный, где состав файла заказывают.
   * Период и отделы он берёт те же, что и экран, так что «то, что на экране» и «то, что
   * в книге» не расходятся молча.
   *
   * CSV уезжает ПРЕЖНЕЙ ручкой: матрица табеля в CSV не ложится, и маршрут её там честно
   * отвергает (см. entities/attendance/api → exportTimesheet).
   */
  const timesheetSlot = (
    <TimesheetPicker
      companyId={companyId}
      period={period}
      periodLabel={periodLabel}
      departmentIds={departmentIds}
      fileName={reportFileName([t('timesheet.title'), periodLabel], '').slice(0, -1)}
      onCsv={() =>
        exportCompanyAttendance(companyId, period, departmentIds, 'csv', i18n.language)
      }
    />
  );

  const openPerson = (recordId: string, name: string) =>
    navigation.navigate('EmployeeReport', {recordId, name, period: periodKey(period)});

  /** Запрос дневных данных предлагается там, где появится результат. */
  const chartOffer = (
    <Card padding={4} style={styles.offer} testID="chart-offer">
      <AppText variant="heading">{t('reports.dailyChart')}</AppText>
      <AppText variant="body" color={p.mutedForeground}>
        {t('reports.chartNeedsDays')}
      </AppText>
      <AppButton
        title={t('reports.showChart')}
        tone="outline"
        size="sm"
        icon="dashboard"
        onPress={() => setIncludeDays(true)}
        style={styles.offerButton}
        testID="reports-show-chart"
      />
      {/* Объём считается от ЭТОЙ выборки. В подсказке была зашита «компания в 200
          человек» и «6 000 строк» — на отчёте по школе в 1304 человека это число
          занижало правду вчетверо, а читатель принимал по нему решение, ждать ли
          загрузку. */}
      <HelpNote
        text={t('reports.includeDaysHint', {
          // `count` выбирает форму слова, `rows` печатается: одно и то же число, но
          // склонять надо по нему самому, а показывать — с разделителем разрядов.
          count: employees.length * periodLength(period),
          rows: formatCount(employees.length * periodLength(period)),
        })}
      />
    </Card>
  );

  let content: React.ReactNode;

  if (report.isPending) {
    content = (
      <View style={styles.loading}>
        <Skeleton height={190} radius={radii.xl} />
        <Skeleton height={140} radius={radii.xl} />
      </View>
    );
  } else if (report.isError) {
    content = (
      <EmptyState
        tone="error"
        title={t('reports.error')}
        actionLabel={t('actions.retry')}
        onAction={() => void report.refetch()}
      />
    );
  } else {
    content = (
      <>
        {/* 1 ── шесть чисел в ряд. Три из них — отбор для таблицы внизу. */}
        <Section>
          <AttendanceKpi
            summary={report.data.summary}
            roster={roster}
            issue={issue}
            onIssue={setIssue}
          />
          <HelpNote text={t('reports.kpi.filterHint')} />
        </Section>

        {/* 2 ── тренд по дням. Грузится сам, пока строк не слишком много. */}
        <Section>
          {wantDays ? (
            daily.isPending ? (
              <Skeleton height={220} radius={radii.xl} />
            ) : (
              <DailyChart
                days={totals}
                // «Скрыть» предлагается только там, где график включили руками:
                // спрятать то, что приехало само и ничего не стоило, незачем.
                onHide={includeDays ? () => setIncludeDays(false) : undefined}
              />
            )
          ) : (
            chartOffer
          )}
        </Section>

        {/* 3 ── таблица. Это то, ради чего в отчёт заходят, и она больше не сложена
            в раскрыватель: до людей доходили через два нажатия, из которых первое
            («По сотрудникам ▸») не сообщало ничего. */}
        {/* Без подписи под заголовком: сколько людей в выборке, уже сказано в шапке
            страницы, а сколько их после отбора — в рейке под таблицей. Третье место с
            тем же числом только заставляет сверять, не разошлись ли они. */}
        <Section title={t('reports.byEmployee')}>
          <EmployeeReportList
            employees={employees}
            companyId={companyId}
            issue={issue}
            onIssueChange={setIssue}
            onOpen={row => openPerson(row.record_id, row.full_name)}
          />
        </Section>

        {/* 4 ── отделы. Больше одного: раздел существует, чтобы СРАВНИВАТЬ их между
            собой, а «Отделов: 1» — это заголовок, раскрывающийся в те же цифры, что
            стоят наверху, только одной строкой. */}
        {deptList.length > 1 ? (
          <Section>
            <Disclosure
              testID="section-departments"
              title={t('reports.byDepartment')}
              summary={t('reports.departmentCount', {count: deptList.length})}
              open={open.departments}
              onToggle={() => setOpen(o => ({...o, departments: !o.departments}))}>
              <HelpNote text={t('reports.byDepartmentHint')} />
              <DepartmentRanking
                companyId={companyId}
                period={period}
                departments={deptList}
                onOpen={dept =>
                  navigation.navigate('DepartmentReport', {
                    deptId: dept.department_id,
                    name: dept.name,
                    period: periodKey(period),
                  })
                }
              />
            </Disclosure>
          </Section>
        ) : null}
      </>
    );
  }

  return (
    <Page
      scroll
      title={t('nav.attendance')}
      subtitle={scopeLine}
      refreshing={report.isRefetching}
      onRefresh={() => void report.refetch()}
      testID="reports-screen"
      toolbar={
        <>
          {salaries.canRead ? (
            <Segmented
              style={styles.segment}
              value="attendance"
              onChange={value => {
                if (value === 'payroll') {
                  navigation.navigate('Payroll');
                }
              }}
              testID="attendance-mode"
              options={[
                {value: 'attendance', label: t('attendance.tabAttendance')},
                {value: 'payroll', label: t('payroll.title')},
              ]}
            />
          ) : null}
          <ReportToolbar
            period={period}
            onPeriodChange={setPeriod}
            now={now}
            periodLabel={periodLabel}
            departments={deptList}
            departmentIds={departmentIds}
            onDepartmentsChange={setDepartmentIds}
            exportSlot={exportSlot}
            timesheetSlot={timesheetSlot}
          />
        </>
      }>
      {content}
    </Page>
  );
}
